import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { useCreateOrder } from '@euler/app/flows/home/functions/useCreateOrder';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  AiAgentBundleId,
  AiHomeActivity,
  AiHomeActivityResult,
  AiHomeFeature,
  AiHomeSession,
  AiHomeSuggestion,
  parseAiBundleHandoff,
} from '@euler/model/ai-home';
import { AiAttachmentInfo } from '@euler/model/apdm';
import { StoreFeature } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import {
  DrawerActions,
  useFocusEffect,
  useIsFocused,
  useNavigation,
} from '@react-navigation/native';
import { useCallback, useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { AiHistorySheet } from './history/AiHistoryList';
import { AiHomeSuggestionsState, AiHomeView } from './home/AiHomeView';
import { useAiTrialCredits } from './home/useAiTrialCredits';
import {
  AiWorkshopToolKind,
  useAiWorkshopTools,
} from './home/useAiWorkshopTools';
import { useFullEditionUpgradeRequest } from './home/useFullEditionUpgradeRequest';
import { openAiChat } from './openAiChat';
import {
  AiSuiteHomeTool,
  AiSuiteTool,
  aiColors,
  aiSuiteToolForFeature,
  aiSuiteToolForSession,
} from './theme';

export interface AiSuitePageParams {
  sessionId?: string;
  bundleId?: AiAgentBundleId;
  prompt?: string;
  requestId?: string;
}

function AiSuiteWorkspace({
  sessionId,
  bundleId,
  prompt,
  requestId,
}: AiSuitePageParams) {
  const profile = useUserProfileInfo();
  const biEnabled = useStoreFeature(StoreFeature.Bi).enabled;
  const mobile = useIsMobileLayout();
  const navigation = useAppNavigation();
  const local = useNavigation();
  const focused = useIsFocused();
  const { aiAssistantService } = useServiceFactory();
  const [suggestions, setSuggestions] = useState<AiHomeSuggestionsState>({
    items: null,
    failed: false,
  });
  const [reload, setReload] = useState(0);
  const [history, setHistory] = useState(false);
  const [activity, setActivity] = useState<AiHomeActivityResult | null>(null);

  // Read again on every return: a campaign paused or a lead taken in a chat
  // changes what the home page should show.
  useFocusEffect(
    useCallback(() => {
      let active = true;
      aiAssistantService
        .getHomeActivity()
        .then(result => {
          if (active) setActivity(result);
        })
        .catch(error => {
          console.error('AI home activity failed', error);
        });
      return () => {
        active = false;
      };
    }, [aiAssistantService]),
  );

  // Links from before the chat had its own route still name a conversation
  // or hand over a prompt here; pass them on to the chat.
  useEffect(() => {
    const value =
      bundleId && prompt && parseAiBundleHandoff({ bundleId, prompt });
    if (!sessionId && !value) return;
    local.setParams({
      sessionId: undefined,
      bundleId: undefined,
      prompt: undefined,
      requestId: undefined,
    } as never);
    if (sessionId) openAiChat(navigation, { kind: 'session', sessionId });
    else if (value)
      openAiChat(navigation, {
        kind: 'new',
        tool: aiSuiteToolForSession(value.bundleId),
        bundleId: value.bundleId,
        message: { text: value.prompt, attachments: [] },
      });
  }, [navigation, local, sessionId, bundleId, prompt, requestId]);

  useEffect(() => {
    let active = true;
    setSuggestions({ items: null, failed: false });
    aiAssistantService
      .getHomeSuggestions()
      .then(result => {
        if (active) setSuggestions({ items: result.items, failed: false });
      })
      .catch(error => {
        console.error('AI home suggestions failed', error);
        if (active) setSuggestions({ items: null, failed: true });
      });
    return () => {
      active = false;
    };
  }, [aiAssistantService, reload]);

  const start = useCallback(
    (
      text: string,
      attachments: AiAttachmentInfo[],
      focus: AiSuiteTool | null,
    ) => {
      openAiChat(navigation, {
        kind: 'new',
        tool: focus,
        message: { text, attachments },
      });
      return true;
    },
    [navigation],
  );
  const openTool = useCallback(
    (picked: AiSuiteHomeTool) => {
      // 朋友圈海报 is the poster studio, framed on a page of its own.
      if (picked.kind === 'page') navigation.navigate('PosterStudio', {});
      // Practice starts from its own page: suggestions and the library.
      else if (picked.feature === AiHomeFeature.Coaching)
        navigation.navigate('Coaching', {});
      // Campaigns are run from their dashboard; it starts a new one in chat.
      else if (picked.bundleId === AiAgentBundleId.Apdm)
        navigation.navigate('ApdmDashboard', {});
      // Analysis lives on dashboards where BI is sold: it opens on the
      // default one, and they ask AI from there.
      else if (
        picked.feature === AiHomeFeature.BusinessIntelligence &&
        biEnabled
      )
        navigation.navigate('BiHome', {});
      else openAiChat(navigation, { kind: 'new', tool: picked });
    },
    [navigation, biEnabled],
  );
  const openSuggestion = useCallback(
    (item: AiHomeSuggestion) => {
      if (!item.available) return;
      const focus = aiSuiteToolForFeature(item.feature);
      start(
        item.prompt,
        [],
        item.agent ? focus ?? aiSuiteToolForSession(item.agent) : null,
      );
    },
    [start],
  );
  const openSession = useCallback(
    (session: AiHomeSession) => {
      setHistory(false);
      openAiChat(navigation, {
        kind: 'session',
        sessionId: session.id,
        bundleId: session.bundleId,
      });
    },
    [navigation],
  );

  const workshop = useAiWorkshopTools();
  const aiTrial = useAiTrialCredits();
  const upgrade = useFullEditionUpgradeRequest();
  const createOrder = useCreateOrder();
  // The workshop screens stay registered beside the AI edition's tabs.
  const openWorkshopTool = useCallback(
    (kind: AiWorkshopToolKind) => {
      if (kind === 'order') {
        createOrder();
        return;
      }
      const tabs = {
        ongoing: 'Dashboard',
        finished: 'History',
        plans: 'Plans',
      };
      local.navigate(tabs[kind] as never);
    },
    [createOrder, local],
  );

  // A campaign opens on its page; the page leads back to its conversation.
  const openActivity = useCallback(
    (item: AiHomeActivity) =>
      navigation.navigate('ApdmCampaign', { campaignId: item.campaignId }),
    [navigation],
  );

  return (
    <View style={{ flex: 1, backgroundColor: aiColors.background }}>
      <AiHomeView
        name={profile?.user.name || profile?.user.nick || ''}
        focused={focused}
        leading={mobile ? 'menu' : null}
        onLeading={() => local.dispatch(DrawerActions.openDrawer())}
        onHistory={() => setHistory(true)}
        onSubmit={(text, attachments) => start(text, attachments, null)}
        onTool={openTool}
        onSuggestion={openSuggestion}
        onRetrySuggestions={() => setReload(value => value + 1)}
        suggestions={suggestions}
        activity={activity}
        wide={!mobile}
        onActivity={openActivity}
        onMoreActivity={() => navigation.navigate('ApdmDashboard', {})}
        workshop={workshop}
        aiTrial={aiTrial}
        onWorkshopTool={openWorkshopTool}
        upgrade={
          upgrade.from != null
            ? { from: upgrade.from, busy: upgrade.busy }
            : null
        }
        onUpgrade={upgrade.request}
      />
      <AiHistorySheet
        visible={history}
        side={!mobile}
        onOpen={openSession}
        onClose={() => setHistory(false)}
      />
    </View>
  );
}

export const AiSuitePage = wrapNavigatorScreen(
  (props: AiSuitePageParams) => {
    const profile = useUserProfileInfo();
    if (!profile?.user.enabled)
      return <Text>当前账号不可用，请重新登录。</Text>;
    return (
      <AiSuiteWorkspace
        key={`${profile.org.id}:${profile.store.id}:${profile.user.userId}`}
        {...props}
      />
    );
  },
  { title: '知车 AI', headerShown: false },
);
